import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Grid,
  Avatar,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import PhotoCameraIcon from "@material-ui/icons/PhotoCamera";
import ImageIcon from "@material-ui/icons/Image";
import * as api from "../../../api";
import { useDispatch } from "react-redux";
import useStyles from "../styles";

const useLocalStyles = makeStyles((theme) => ({
  picturesContainer: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.type === 'dark' ? '#2e2e2e' : '#f9f9f9',
    borderRadius: "8px",
    [theme.breakpoints.down('xs')]: {
      backgroundColor: 'transparent',
      padding: 0,
    },
  },
  uploadSection: {
    backgroundColor: theme.palette.type === 'dark' ? '#424242' : '#fff',
    padding: theme.spacing(2),
    borderRadius: "8px",
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(3),
  },
  uploadButton: {
    marginRight: theme.spacing(1),
  },
  picturesGrid: {
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      margin: 0,
      width: '100%',
    },
  },
  pictureCard: {
    position: "relative",
    "&:hover .deleteButton": {
      display: "flex",
    },
  },
  deleteButton: {
    display: "none",
    position: "absolute",
    top: 8,
    right: 8,
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    zIndex: 1,
  },
  pictureImage: {
    height: 200,
    objectFit: "cover",
    maxWidth: "100%",
    cursor: "pointer",
  },
  lightboxImage: {
    maxWidth: "100%",
    maxHeight: "80vh",
    display: "block",
    margin: "0 auto",
    borderRadius: "4px",
  },
  cardContent: {
    padding: theme.spacing(1),
  },
  userName: {
    fontWeight: "bold",
    fontSize: "0.9rem",
  },
  uploadTime: {
    fontSize: "0.75rem",
    color: theme.palette.type === 'dark' ? '#bbb' : '#767676',
    marginTop: theme.spacing(0.5),
  },
  hiddenInput: {
    display: "none",
  },
  loginPrompt: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(3),
    backgroundColor: theme.palette.type === 'dark' ? '#424242' : '#fff',
    borderRadius: "8px",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(3),
  },
  noPicturesMessage: {
    padding: theme.spacing(3),
    textAlign: "center",
    backgroundColor: theme.palette.type === 'dark' ? '#424242' : '#f9f9f9',
    borderRadius: "8px",
    border: `1px solid ${theme.palette.divider}`,
    transition: "background-color 0.3s ease",
  },
}));

const CoursePicturesSection = ({ course }) => {
  const classes = useLocalStyles();
  const globalClasses = useStyles();
  const fileInputRef = useRef();
  const [pictures, setPictures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingPictures, setFetchingPictures] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [picIdToDelete, setPicIdToDelete] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const user = JSON.parse(localStorage.getItem("profile"));

  // Fetch pictures on component mount
  useEffect(() => {
    if (course && course._id) {
      fetchPictures();
    }
  }, [course._id]);

  const fetchPictures = async () => {
    if (!course || !course._id) return;

    try {
      setFetchingPictures(true);
      const { data } = await api.getCoursePictures(course._id);
      setPictures(data || []);
    } catch (error) {
      console.error("Error fetching pictures:", error);
    } finally {
      setFetchingPictures(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!validTypes.includes(file.type)) {
      alert("Please upload a valid image file (JPG, PNG, WebP, or GIF)");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("Image size must be less than 5MB");
      return;
    }

    uploadPicture(file);
  };

  const uploadPicture = async (file) => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("image", file);

      const response = await api.uploadCoursePicture(course._id, formData);

      // Add new picture to the list
      setPictures([response.data.picture, ...pictures]);

      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      alert("Error uploading picture: " + error.message);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (picId) => {
    setPicIdToDelete(picId);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      await api.deleteCoursePicture(course._id, picIdToDelete);

      // Remove picture from list
      setPictures(pictures.filter((pic) => pic.picId !== picIdToDelete));
      setDeleteConfirmOpen(false);
      setPicIdToDelete(null);
    } catch (error) {
      console.error("Failed to delete picture:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <>
        <Typography variant="h6" style={{ paddingBottom: '16px', marginTop: 64 }}>
          Pictures ({pictures.length})
        </Typography>
        <Box className={classes.picturesContainer}>

          {/* Pictures Grid for guest users */}
          {fetchingPictures ? (
            <Box className={classes.loadingContainer}>
              <CircularProgress />
            </Box>
          ) : pictures.length === 0 ? (
            <Box className={classes.noPicturesMessage}>
              <Typography variant="body2" color="textSecondary">
                No pictures yet. Be the first to upload one!
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2} className={classes.picturesGrid}>
              {pictures.map((picture) => (
                <Grid item xs={12} sm={6} md={4} key={picture.picId}>
                  <Card className={classes.pictureCard}>
                    <CardMedia
                      component="img"
                      image={picture.image}
                      alt="Course"
                      className={classes.pictureImage}
                      onClick={() => setLightboxImage(picture.image)}
                    />
                    <CardContent className={classes.cardContent}>
                      <Typography className={classes.userName}>
                        {picture.userName}
                      </Typography>
                      <Typography className={classes.uploadTime}>
                        {new Date(picture.createdAt).toLocaleDateString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          <Box className={classes.loginPrompt} style={{marginTop: '16px'}}>
            <Typography variant="body2" color="textSecondary">
              <Typography
                component={Link}
                to="/auth"
                className={globalClasses.logout}
                variant="inherit"
                color="primary"
              >
                Sign in
              </Typography>
              {" "}to add a course picture
            </Typography>
          </Box>
        </Box>
      </>
    );
  }

  return (
    <>
      <Typography variant="h6" style={{ paddingBottom: '16px', marginTop: 64 }}>
        Pictures ({pictures.length})
      </Typography>
      <Box className={classes.picturesContainer}>
        {/* Pictures Grid */}
        {fetchingPictures ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        ) : pictures.length === 0 ? (
          <Box className={classes.noPicturesMessage}>
            <Typography variant="body2" color="textSecondary">
              No pictures yet. Be the first to upload one!
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2} className={classes.picturesGrid}>
            {pictures.map((picture) => (
              <Grid item xs={12} sm={6} md={4} key={picture.picId}>
                <Card className={classes.pictureCard}>
                  <CardMedia
                    component="img"
                    image={picture.image}
                    alt="Course"
                    className={classes.pictureImage}
                    onClick={() => setLightboxImage(picture.image)}
                  />

                  {/* Delete Button */}
                  {user.result._id === picture.userId && (
                    <IconButton
                      size="small"
                      color="secondary"
                      className={`${classes.deleteButton} deleteButton`}
                      onClick={() => handleDeleteClick(picture.picId)}
                      disabled={loading}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}

                  <CardContent className={classes.cardContent}>
                    <Typography className={classes.userName}>
                      {picture.userName}
                    </Typography>
                    <Typography className={classes.uploadTime}>
                      {new Date(picture.createdAt).toLocaleDateString()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Upload Section */}
        <Box className={classes.uploadSection} style={{ marginTop: 24, marginBottom: 0, backgroundColor: "transparent", border: "none", padding: 0 }}>
          <Box style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<PhotoCameraIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={loading}
              className={classes.uploadButton}
              style={{ alignSelf: "flex-start", whiteSpace: "nowrap" }}
            >
              {loading ? "Uploading..." : "Choose Image"}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className={classes.hiddenInput}
              onChange={handleFileSelect}
            />
            <Typography variant="caption" color="textSecondary">
              Max file size: 5MB. Supported formats: JPG, PNG, WebP, GIF
            </Typography>
          </Box>
        </Box>

        {/* Lightbox Dialog */}
        <Dialog open={Boolean(lightboxImage)} onClose={() => setLightboxImage(null)} maxWidth="lg" fullWidth>
          <DialogContent style={{ padding: "8px" }}>
            <img src={lightboxImage} alt="Full size" className={classes.lightboxImage} />
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
        >
          <DialogTitle>Delete Picture?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Are you sure you want to delete this picture?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button
              onClick={handleConfirmDelete}
              color="secondary"
              disabled={loading}
            >
              {loading ? "Deleting..." : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default CoursePicturesSection;
